import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Search, X, Check, Filter } from 'lucide-react';
import { Vendor, VendorSelectorProps } from '../types/vendor';
import { mockVendors } from '../data/mockVendors';
import { mockEntities, mockSites } from '../data/mockEntitiesAndSites';
import { Badge } from './ui/Badge';
export function VendorSelector({
  value,
  onChange,
  currentEntityId,
  currentSiteId,
  placeholder = 'Search vendors...',
  disabled = false,
  error
}: VendorSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [scopeFilter, setScopeFilter] = useState<'all' | 'entity' | 'site' | null>(null);
  const [selectedEntityFilter, setSelectedEntityFilter] = useState<string | null>(null);
  const [selectedSiteFilter, setSelectedSiteFilter] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Memoized filtered vendors for performance with large datasets
  const filteredVendors = useMemo(() => {
    return mockVendors.filter(vendor => {
      if (!vendor.isActive) return false;
      // Apply scope filter
      if (scopeFilter && vendor.scope !== scopeFilter) {
        return false;
      }
      // Apply entity filter - only filter if entity is selected
      if (selectedEntityFilter) {
        if (vendor.scope === 'entity') {
          if (!vendor.entityIds?.includes(selectedEntityFilter)) return false;
        } else if (vendor.scope === 'site') {
          // For site-scoped vendors, check if any of their sites belong to the selected entity
          const vendorSites = mockSites.filter(site => vendor.siteIds?.includes(site.id));
          if (!vendorSites.some(site => site.entityId === selectedEntityFilter)) return false;
        }
        // 'all' scope vendors are always included regardless of entity filter
      }
      // Apply site filter - only filter if site is selected
      if (selectedSiteFilter && vendor.scope === 'site') {
        if (!vendor.siteIds?.includes(selectedSiteFilter)) return false;
      }
      // Check search query - includes code, name, and taxId
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return vendor.code.toLowerCase().includes(query) || vendor.name.toLowerCase().includes(query) || vendor.taxId && vendor.taxId.toLowerCase().includes(query);
      }
      return true;
    });
  }, [scopeFilter, selectedEntityFilter, selectedSiteFilter, searchQuery]);
  const selectedVendor = value ? mockVendors.find(v => v.id === value) : null;
  // Reset highlighted index when filtered list changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredVendors]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && !inputRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < filteredVendors.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredVendors[highlightedIndex]) {
          handleSelect(filteredVendors[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };
  const handleSelect = (vendor: Vendor) => {
    onChange(vendor.id);
    setSearchQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };
  const handleClear = () => {
    onChange(null);
    setSearchQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  };
  const getScopeBadge = (vendor: Vendor) => {
    switch (vendor.scope) {
      case 'all':
        return <Badge variant="default" data-id="element-2100">All Sites</Badge>;
      case 'entity':
        return <Badge variant="secondary" data-id="element-2101">Entity Only</Badge>;
      case 'site':
        return <Badge variant="outline" data-id="element-2102">Site Specific</Badge>;
      default:
        return null;
    }
  };
  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return <>
        {parts.map((part, i) => part.toLowerCase() === query.toLowerCase() ? <mark key={i} className="bg-blue-100 text-blue-900 font-medium" data-id="element-2103">
              {part}
            </mark> : <span key={i} data-id="element-2104">{part}</span>)}
      </>;
  };
  // Memoized scope counts for performance
  const scopeCounts = useMemo(() => ({
    all: mockVendors.filter(v => v.isActive && v.scope === 'all').length,
    entity: mockVendors.filter(v => v.isActive && v.scope === 'entity').length,
    site: mockVendors.filter(v => v.isActive && v.scope === 'site').length
  }), []);
  // Get available sites based on selected entity filter
  const availableSites = useMemo(() => {
    return selectedEntityFilter ? mockSites.filter(site => site.entityId === selectedEntityFilter) : mockSites;
  }, [selectedEntityFilter]);
  // Limit displayed results for performance (show first 100, user should search to narrow down)
  const displayedVendors = filteredVendors.slice(0, 100);
  const hasMoreResults = filteredVendors.length > 100;
  return <div className="relative" data-id="element-2105">
      <div className="relative" data-id="element-2106">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2107">
          <Search className="w-4 h-4" data-id="element-2108" />
        </div>
        <input ref={inputRef} type="text" value={isOpen ? searchQuery : selectedVendor ? `${selectedVendor.code} - ${selectedVendor.name}` : ''} onChange={e => {
        setSearchQuery(e.target.value);
        if (!isOpen) setIsOpen(true);
      }} onFocus={() => {
        setIsOpen(true);
        if (selectedVendor) setSearchQuery('');
      }} onKeyDown={handleKeyDown} placeholder={placeholder} disabled={disabled} className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'} ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`} aria-label="Vendor selector" aria-expanded={isOpen} aria-controls="vendor-dropdown" role="combobox" data-id="element-2109" />
        {selectedVendor && !disabled && <button type="button" onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Clear selection" data-id="element-2110">
            <X className="w-4 h-4" data-id="element-2111" />
          </button>}
      </div>

      {error && <p className="mt-1 text-sm text-red-600" data-id="element-2112">{error}</p>}

      {isOpen && <div ref={dropdownRef} id="vendor-dropdown" className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden flex flex-col" role="listbox" data-id="element-2113">
          {/* Scope Filters */}
          <div className="border-b border-gray-200 p-3 bg-gray-50" data-id="element-2114">
            <div className="flex items-center gap-2 mb-2" data-id="element-2115">
              <Filter className="w-3.5 h-3.5 text-gray-500" data-id="element-2116" />
              <span className="text-xs font-medium text-gray-600 uppercase" data-id="element-2117">
                Filter by Scope
              </span>
            </div>
            <div className="flex gap-2 mb-3" data-id="element-2118">
              <button type="button" onClick={() => {
            setScopeFilter(null);
            setSelectedEntityFilter(null);
            setSelectedSiteFilter(null);
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${scopeFilter === null ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-2119">
                All ({mockVendors.filter(v => v.isActive).length})
              </button>
              <button type="button" onClick={() => {
            setScopeFilter('all');
            setSelectedEntityFilter(null);
            setSelectedSiteFilter(null);
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${scopeFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-2120">
                All Sites ({scopeCounts.all})
              </button>
              <button type="button" onClick={() => {
            setScopeFilter('entity');
            setSelectedSiteFilter(null);
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${scopeFilter === 'entity' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-2121">
                Entity ({scopeCounts.entity})
              </button>
              <button type="button" onClick={() => {
            setScopeFilter('site');
            setSelectedEntityFilter(null);
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${scopeFilter === 'site' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`} data-id="element-2122">
                Site ({scopeCounts.site})
              </button>
            </div>

            {/* Entity Selector - shown when Entity scope is selected OR when filtering sites */}
            {(scopeFilter === 'entity' || scopeFilter === 'site') && <div className="mb-2" data-id="element-2123">
                <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-2124">
                  {scopeFilter === 'entity' ? 'Select Entity:' : 'Filter by Entity (optional):'}
                </label>
                <select value={selectedEntityFilter || ''} onChange={e => {
            setSelectedEntityFilter(e.target.value || null);
            if (scopeFilter === 'site') {
              setSelectedSiteFilter(null); // Reset site when entity changes
            }
          }} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2125">
                  <option value="" data-id="element-2126">All Entities</option>
                  {mockEntities.map(entity => <option key={entity.id} value={entity.id} data-id="element-2127">
                      {entity.code} - {entity.name}
                    </option>)}
                </select>
              </div>}

            {/* Site Selector - shown when Site scope is selected */}
            {scopeFilter === 'site' && <div data-id="element-2128">
                <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-2129">
                  Select Site:
                </label>
                <select value={selectedSiteFilter || ''} onChange={e => setSelectedSiteFilter(e.target.value || null)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-2130">
                  <option value="" data-id="element-2131">All Sites</option>
                  {availableSites.map(site => <option key={site.id} value={site.id} data-id="element-2132">
                      {site.code} - {site.name}
                    </option>)}
                </select>
              </div>}
          </div>

          {/* Results count and search hint */}
          {filteredVendors.length > 0 && <div className="px-4 py-2 bg-blue-50 border-b border-blue-100" data-id="element-2133">
              <p className="text-xs text-blue-700" data-id="element-2134">
                {filteredVendors.length === 1 ? '1 vendor found' : `${filteredVendors.length} vendors found`}
                {hasMoreResults && <span className="ml-1 text-blue-600" data-id="element-2135">
                    (showing first 100 - use search to narrow results)
                  </span>}
              </p>
            </div>}

          {/* Vendor List */}
          <div className="flex-1 overflow-auto" data-id="element-2136">
            {displayedVendors.length === 0 ? <div className="px-4 py-8 text-center text-gray-500" data-id="element-2137">
                <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" data-id="element-2138" />
                <p className="text-sm font-medium" data-id="element-2139">No vendors found</p>
                <p className="text-xs mt-1" data-id="element-2140">
                  {searchQuery || scopeFilter || selectedEntityFilter || selectedSiteFilter ? 'Try adjusting your search or filters' : 'No vendors available'}
                </p>
              </div> : <div className="py-1" data-id="element-2141">
                {displayedVendors.map((vendor, index) => <button key={vendor.id} type="button" onClick={() => handleSelect(vendor)} onMouseEnter={() => setHighlightedIndex(index)} className={`w-full px-4 py-3 text-left transition-colors ${index === highlightedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'} ${value === vendor.id ? 'bg-blue-50' : ''}`} role="option" aria-selected={value === vendor.id} data-id="element-2142">
                    <div className="flex items-start justify-between gap-3" data-id="element-2143">
                      <div className="flex-1 min-w-0" data-id="element-2144">
                        <div className="flex items-center gap-2 mb-1" data-id="element-2145">
                          <span className="font-mono text-sm font-medium text-gray-900" data-id="element-2146">
                            {highlightMatch(vendor.code, searchQuery)}
                          </span>
                          {getScopeBadge(vendor)}
                        </div>
                        <p className="text-sm text-gray-700 truncate" data-id="element-2147">
                          {highlightMatch(vendor.name, searchQuery)}
                        </p>
                        {vendor.taxId && <p className="text-xs text-gray-500 mt-1" data-id="element-2148">
                            Tax ID: {highlightMatch(vendor.taxId, searchQuery)}
                          </p>}
                        {vendor.paymentTerms && <p className="text-xs text-gray-500 mt-0.5" data-id="element-2149">
                            {vendor.paymentTerms}
                          </p>}
                      </div>
                      {value === vendor.id && <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-2150" />}
                    </div>
                  </button>)}
              </div>}
          </div>
        </div>}
    </div>;
}